import { useEffect, useState } from "react";
import { IconButton, Menu } from "@mui/material";
import { tss } from "tss-react";
import { LayersActions, ViewerActions } from "actions";
import ViewerEvents from "events/ViewerEvents";
import { getMeshProperties, getEntityBounds } from "actions/GlobalActions";
import { handleLayers, handleLayerEvent, getZoomToLayersIds } from "actions/ViewerTools";
import { LayerIcon, EditableLabel, Toolbar, ConfirmationDialog } from "components";
import { MeshProperty, MAX_MESHSET_COUNT } from "constants/viewer";
import Set from "./Set";
import { ArrowRight, ArrowDropDown, MoreVert } from "@mui/icons-material";
import commonStyles, { backgroundColor, hoverColor, selectedLayerColor } from "../styles/common";
import {  useDispatch } from 'react-redux';
import { setData } from "store/main/viewerHistory";

const useStyles = tss.create((theme) => ({
    root: {
        position: "relative",
        display: "flex",
        height: 45,
        width: 300,
        boxShadow: "inset 0 -1px 0 0 rgba(0,0,0,0.1)",
        cursor: "pointer",
        transition: "opacity 2s",
        "&:hover": {
            backgroundColor: hoverColor,
            transition: "opacity 0.1s",
        },
    },
    selected: {
        color: "rgb(0, 117, 176)",
        background: "rgba(0,39,118,0.1)",
    },
    layerName: {
        display: "flex",
        width: "calc(100% - 80px)",
    },
    layerTitle: {
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
        color: "#283C50",
        fontSize: 14,
        letterSpacing: 0,
        lineHeight: "16px",
    },
    hiddenLayerTitle: {
        color: "#434343",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
        pointerEvents: "none",
        opacity: 0.25,
    },
    arrows: {
        display: "inline-flex",
        alignItems: "center",
        height: "100%",
        width: 24,
    },
    layerTitleContainer: {
        display: "inline-flex",
        position: "relative",
        alignItems: "center",
        width: 210,
        maxWidth: "65%",
    },
    layerSelected: {
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
        color: selectedLayerColor,
        fontSize: 14,
        letterSpacing: 0,
        lineHeight: "16px",
    },
    rightDiv: {
        position: "absolute",
        display: "inline-flex",
        alignItems: "center",
        right: 12,
        width: 36,
        height: "100%",
    },
}));

class DragActions {
    static handleDragEnter = (e: any, layer: any) => {
        e.stopPropagation();
        e.preventDefault();
        LayersActions.draggedLayerId = layer.ID;
    };
    static handleDragLeave = (e: any) => {
        e.preventDefault();
        LayersActions.draggedLayerId = "";
    };
    static handleDragOver = (e: any) => {
        e.preventDefault();
    };
    static handleDrop = (e: any, layer: any, callback: () => void) => {
        e.preventDefault();
        e.stopPropagation();
        LayersActions.dropLayer(LayersActions.draggedLayerId, layer.ID, callback);
    };
}

class MeshActions {
    props: any;
    meshProperty: MeshProperty;
    constructor(props: any) {
        this.props = props;
        this.meshProperty = getMeshProperties(props.layer);
    }

    updateMeshProperties = (meshProperty: MeshProperty) => {
        var { layer } = this.props;
        if (ViewerEvents.viewer && layer.parent_layer) {
            ViewerActions.setMeshProperties(layer, meshProperty, true);
        }
    };

    toggleVisibility = () => {
        var { meshProperty } = this;
        var { layer } = this.props;
        if (layer.not_selectable) {
            var newProp = {
                ...meshProperty,
                meshIndex: layer.index,
                meshVisible: !meshProperty.meshVisible,
            };
            layer["layer_visible"] = !newProp.meshVisible ? "0" : "1";
            this.updateMeshProperties(newProp);
            this.props.updateParent();
        } else {
            this.props.toggleVisibility(!meshProperty.meshVisible, layer.ID);
        }
    };

    setTransparency = (opacity: number) => {
        var { meshProperty } = this;
        var { layer } = this.props;
        if (layer.not_selectable) {
            var newProp = {
                ...meshProperty,
                meshIndex: layer.index,
                meshOpacity: opacity * 0.01,
            };
            layer["layer_opacity"] = newProp.meshOpacity + "";
            this.updateMeshProperties(newProp);
            this.props.updateParent();
        } else {
            this.props.setTransparency(opacity, layer.ID);
        }
    };

    deleteLayer = (object: any, callback: () => void) => {
        if (object.status) {
            var { layer } = this.props;
            handleLayerEvent("deleteLayer", null, layer);
            var layers = LayersActions.layers;
            var index = Object.values(layers).findIndex((curLayer: any) => curLayer.ID === layer.ID);
            delete layers[index];
            handleLayers({ ...layers });
            LayersActions.needsUpdate = true;
            LayersActions.emit("updateTree", layers);
        }
        callback();
    };

    zoomToLayer = () => {
        var { layer, currentProject, inputs } = this.props;
        var zoomEntities = getZoomToLayersIds([layer], inputs);
        getEntityBounds(zoomEntities, currentProject, (bounds: any) => {
            console.log("bounds:", bounds);
            handleLayerEvent("frameWorldBounds", bounds, layer);
        });
    };
}

type IProps = {
    layer: any;
    currentProject: any;
    inputs: any;
    selectedLayers: any;
    setSelectedLayers: (layer: any) => void;
    toggleSelectedLayers: (layer: any) => void;
    setLayerId: (layerId: string) => void;
    updateParent: () => void;
    toggleVisibility: (visible: boolean, layerId: string) => void;
    setTransparency: (value: number, layerId: string) => void;
    type?: string;
    folderLevel?: number;
    folderStatus?: string;
    folderHidden?: boolean;
    selectable?: boolean;
    draggable?: boolean;
    childrenCount?: number;
    parentLayer?: any;
    index?: number;
};

export default function Layer(props: IProps) {
    const { classes: common } = commonStyles();
    const { classes, cx } = useStyles();
    const {
        layer,
        currentProject,
        selectedLayers,
        type,
        folderLevel,
        folderStatus,
        folderHidden,
        draggable,
        childrenCount,
        setSelectedLayers,
        toggleSelectedLayers,
        setLayerId,
        updateParent,
    } = props;
    const layerId = layer.ID;
    const isSelected = selectedLayers.find((selectedLayer: any) => selectedLayer.ID === layer.ID);
    const isVisible = layer.layer_visible ? layer.layer_visible === "1" : true;
    const opacity = layer.layer_opacity ? layer.layer_opacity * 100 : 100;
    const disabledArrows = childrenCount && childrenCount > MAX_MESHSET_COUNT;
    var isCollapsed = false;
    const [status, setStatus] = useState("collapse");
    const [anchorEl, setAnchorEl] = useState(null);
    const [update, setUpdate] = useState(false);
    const [needsUpdate, setNeedsUpdate] = useState(false);
    const [openConfirmation, setOpenConfirmation] = useState(false);
    const dispatch = useDispatch();

    var onUpdate = (layer: any) => {
        //console.log('update.Layer', layer);
        setUpdate((prevUpate) => !prevUpate);
    };
    useEffect(() => {
        LayersActions.on("updateLayer", onUpdate);
        return () => {
            LayersActions.removeEventListener("updateLayer", onUpdate);
        };
    }, [update]);

    useEffect(() => {
        if (needsUpdate && layer.not_selectable) {
            setNeedsUpdate(false);
        }
    }, [needsUpdate, layer.not_selectable]);

    if (layer.view_type === "folder") {
        return <div />;
    }

    if (folderStatus) {
        isCollapsed = folderStatus === "collapse" || Boolean(folderHidden);
    }
    const meshActions = new MeshActions(props);
    const selectLayer = (event: any, layerID: string) => {
        if (layerID !== layer.ID || event.target.tagName === "path") {
            return;
        }

        if (event.shiftKey) {
            if (LayersActions.startLayerSelect !== null) {
                LayersActions.addSelectedLayers(layer, (selectedLayers: any) => {
                    setSelectedLayers(selectedLayers);
                });
            }
        } else if (event.ctrlKey) {
            toggleSelectedLayers(layer);
        } else {
            LayersActions.startLayerSelect = layer;
            setSelectedLayers([layer]);
        }
        setLayerId(layer.ID);
    };

    const toggleOpen = () => {
        var state = status === "expand" ? "collapse" : "expand";
        setStatus(state);
    };

    const renameLayer = (value: string) => {
        var view: any = Object.values(currentProject).find((elm: any) => elm.file_type === "VIEW");
        LayersActions.renameFolder(layer.ID, view.project, value, () => {
            dispatch(setData({ view }));
        });
    };

    const handleKebab = (evt: any) => {
        setAnchorEl(evt.currentTarget);
        setSelectedLayers([]);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const confirmDelete = () => {
        setOpenConfirmation(true);
    };

    return (
        <div>
            <div
                className={cx(classes.root, isSelected ? classes.selected : "")}
                style={{
                    display: isCollapsed ? "none" : "flex",
                    paddingLeft: !folderLevel || folderLevel === 0 ? 0 : folderLevel * 11 + "px",
                }}
                draggable={draggable}
                onDrag={(evt: any) => DragActions.handleDragEnter(evt, layer)}
                onDragOver={DragActions.handleDragOver}
                onDragLeave={DragActions.handleDragLeave}
                onDrop={(evt: any) =>
                    DragActions.handleDrop(evt, layer, () => {
                        updateParent();
                        setSelectedLayers([]);
                    })
                }
            >
                <div className={classes.layerName} onClick={(e) => selectLayer(e, layer.ID)}>
                    <LayerIcon
                        {...props}
                        margin={true}
                        className={isVisible ? classes.layerTitle : classes.hiddenLayerTitle}
                    />
                    {type === "set" ? (
                        <div
                            className={cx(
                                classes.arrows,
                                isVisible ? "" : common.disabled,
                                !disabledArrows ? "" : common.disabledEvents
                            )}
                        >
                            {status === "expand" ? (
                                <ArrowDropDown onClick={() => toggleOpen()} />
                            ) : (
                                <ArrowRight onClick={() => toggleOpen()} />
                            )}
                        </div>
                    ) : (
                        ""
                    )}
                    <div
                        id={"layerContainer-" + layerId}
                        className={classes.layerTitleContainer}
                        style={{ left: type === "set" ? 0 : 12 }}
                    >
                        <EditableLabel
                            initialValue={childrenCount ? layer.name + " (" + childrenCount + ")" : layer.name}
                            onBlur={renameLayer}
                            editable={true}
                            className={
                                isVisible
                                    ? isSelected
                                        ? classes.layerSelected
                                        : classes.layerTitle
                                    : classes.hiddenLayerTitle
                            }
                        ></EditableLabel>
                    </div>
                </div>
                <div className={classes.rightDiv}>
                    <IconButton edge="end" aria-label="more" onClick={handleKebab}>
                        <MoreVert />
                    </IconButton>
                </div>
                <Menu
                    anchorEl={anchorEl}
                    keepMounted
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    anchorOrigin={{
                        vertical: "top",
                        horizontal: "right",
                    }}
                >
                    <Toolbar
                        style={{ backgroundColor: backgroundColor }}
                        isVisible={isVisible}
                        opacity={opacity}
                        toggleVisibility={meshActions.toggleVisibility}
                        setTransparency={(evt: any) => meshActions.setTransparency(evt.target.value)}
                        confirmDelete={confirmDelete}
                        zoomToLayer={meshActions.zoomToLayer}
                    />
                </Menu>
            </div>
            {status === "expand" && !layer.not_selectable ? <Set {...props} parentLayer={layer} /> : ""}
            {openConfirmation ? (
                <ConfirmationDialog
                    open={openConfirmation}
                    title="Delete Image"
                    type="image"
                    name={layer.name}
                    confirmAction={(object: any) => meshActions.deleteLayer(object, () => setOpenConfirmation(false))}
                />
            ) : (
                ""
            )}
        </div>
    );
}
